import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  checkAdminLoginAvailable,
  selfRegisterBusiness,
  validateSignupPassword,
} from "../api/selfRegister";
import {
  getCachedTurnstileSiteKey,
  loadTurnstileSiteKey,
} from "../config/turnstilePublic";
import { useTurnstile } from "../hooks/useTurnstile";
import { CountryPicker } from "../components/CountryPicker";
import { getCountryPreset } from "../constants/businessCountryPresets";
import { useLanguage } from "../i18n/LanguageContext";

/** Canonical category names (same as mobile platform targets). */
const CATEGORY_VALUES = [
  "Pharmacie",
  "Boutique",
  "Restaurant",
  "Fastfood",
  "Bar/Terrasse",
  "Hotel",
  "Commerce général",
];

function businessNameWords(name) {
  return (
    String(name ?? "")
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .toLowerCase()
      .match(/[a-z0-9]+/g) || []
  );
}

/** Short code only (no admin@). */
function normalizeLoginShortCode(raw) {
  return String(raw ?? "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 32);
}

function shortCodeFromLoginOrRaw(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s.startsWith("admin@")) return normalizeLoginShortCode(s.slice(6));
  if (s.includes("@")) return normalizeLoginShortCode(s.slice(s.indexOf("@") + 1));
  return normalizeLoginShortCode(s);
}

function buildAdminLogin(shortCode) {
  const code = normalizeLoginShortCode(shortCode);
  return code ? `admin@${code}` : "";
}

function isValidLoginShortCode(shortCode) {
  return /^[a-z0-9][a-z0-9-]{1,31}$/.test(String(shortCode ?? "").trim());
}

/** Preferred short code to auto-fill: first word. */
function suggestLoginShortCode(name) {
  const words = businessNameWords(name);
  if (words.length === 0) return "";
  return words[0].slice(0, 32);
}

/**
 * Placeholder short codes from business name words, comma-separated.
 */
function suggestLoginShortCodePlaceholder(name) {
  const words = businessNameWords(name);
  if (words.length === 0) return "";
  return words.map((w) => w.slice(0, 24)).join(", ");
}

function FieldIcon({ name }) {
  return <i className={`fa-solid ${name} signup-field-icon`} aria-hidden="true" />;
}

function PasswordToggle({ shown, onToggle, showLabel, hideLabel }) {
  return (
    <button
      type="button"
      className="signup-eye-btn"
      onClick={onToggle}
      aria-label={shown ? hideLabel : showLabel}
      tabIndex={0}
    >
      <i className={`fa-solid ${shown ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true" />
    </button>
  );
}

export function InscriptionPage({ Layout }) {
  const { t, lang } = useLanguage();
  const s = t.signup;
  const [searchParams] = useSearchParams();
  const [siteKey, setSiteKey] = useState(() => getCachedTurnstileSiteKey());
  const [siteKeyLoading, setSiteKeyLoading] = useState(!getCachedTurnstileSiteKey());
  const turnstile = useTurnstile(siteKey);
  const captchaEnabled = turnstile.enabled;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSiteKeyLoading(true);
      const key = await loadTurnstileSiteKey();
      if (!cancelled) {
        setSiteKey(key);
        setSiteKeyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const requestedPlan = useMemo(() => {
    const raw = String(searchParams.get("plan") ?? "trial").toLowerCase();
    if (raw === "standard" || raw === "premium" || raw === "enterprise") return raw;
    return "trial";
  }, [searchParams]);

  const [businessName, setBusinessName] = useState("");
  const [loginShortCode, setLoginShortCode] = useState("");
  const [loginTouched, setLoginTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPassword2, setAdminPassword2] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [countryCode, setCountryCode] = useState("CD");
  const [businessAddress, setBusinessAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  /** idle | checking | available | taken | invalid */
  const [loginAvailability, setLoginAvailability] = useState("idle");
  const loginCheckSeq = useRef(0);

  const planLabel =
    requestedPlan === "premium"
      ? t.pricing.plans.premium.name
      : requestedPlan === "standard"
        ? t.pricing.plans.standard.name
        : requestedPlan === "enterprise"
          ? t.pricing.plans.enterprise.name
          : s.trialLabel;

  const categoryOptions = useMemo(
    () =>
      CATEGORY_VALUES.map((value) => ({
        value,
        label: s.categoryOptions?.[value] ?? value,
      })),
    [s.categoryOptions]
  );

  const adminLogin = useMemo(() => buildAdminLogin(loginShortCode), [loginShortCode]);

  const loginPlaceholder = useMemo(() => {
    return suggestLoginShortCodePlaceholder(businessName) || s.loginShortPlaceholder;
  }, [businessName, s.loginShortPlaceholder]);

  function onBusinessNameChange(value) {
    setBusinessName(value);
    if (loginTouched) return;
    const words = businessNameWords(value);
    setLoginShortCode(words.length === 1 ? suggestLoginShortCode(value) : "");
  }

  function onShortCodeChange(value) {
    setLoginTouched(true);
    setLoginShortCode(shortCodeFromLoginOrRaw(value));
  }

  useEffect(() => {
    if (!loginShortCode) {
      setLoginAvailability("idle");
      return undefined;
    }
    if (!isValidLoginShortCode(loginShortCode)) {
      setLoginAvailability("invalid");
      return undefined;
    }

    const login = buildAdminLogin(loginShortCode);
    setLoginAvailability("checking");
    const seq = ++loginCheckSeq.current;
    const timer = window.setTimeout(async () => {
      const res = await checkAdminLoginAvailable(login);
      if (seq !== loginCheckSeq.current) return;
      if (!res.ok) {
        setLoginAvailability("available");
        return;
      }
      if (res.invalid) {
        setLoginAvailability("invalid");
        return;
      }
      setLoginAvailability(res.available ? "available" : "taken");
    }, 450);

    return () => window.clearTimeout(timer);
  }, [loginShortCode]);

  const loginHasError =
    loginAvailability === "taken" ||
    (Boolean(loginShortCode) && loginAvailability === "invalid");
  const passwordMismatch =
    Boolean(adminPassword2) &&
    adminPassword !== adminPassword2;
  const createDisabled =
    busy ||
    loginAvailability === "taken" ||
    loginAvailability === "checking" ||
    loginAvailability === "invalid" ||
    !loginShortCode ||
    passwordMismatch ||
    (captchaEnabled && !turnstile.token);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!isValidLoginShortCode(loginShortCode) || !adminLogin) {
      setError(s.loginInvalid);
      return;
    }
    if (loginAvailability === "taken" || loginAvailability === "checking") {
      return;
    }
    if (adminPassword !== adminPassword2) {
      setError(s.passwordMismatch);
      return;
    }
    const pwd = validateSignupPassword(adminPassword);
    if (!pwd.ok) {
      if (pwd.code === "weak") setError(s.passwordWeak);
      else setError(s.passwordShort);
      return;
    }
    if (captchaEnabled && !turnstile.token) {
      setError(s.captchaRequired);
      return;
    }
    setBusy(true);
    const res = await selfRegisterBusiness({
      businessName,
      adminEmail,
      adminPassword,
      adminLogin,
      categoryName,
      countryCode,
      locationAddress: businessAddress,
      requestedPlan,
      lang,
      website,
      turnstileToken: turnstile.token,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error || s.genericError);
      turnstile.reset();
      return;
    }
    setDone({
      pending: res.pending !== false,
      emailSent: res.emailSent,
      adminLogin: res.adminLogin || adminLogin,
    });
  }

  return (
    <Layout>
      <section className="signup-section">
        <div className="signup-shell">
          <aside className="signup-panel">
            <div className="signup-panel-badge">
              <img src="/images/talo-logo.png" alt="" className="signup-panel-logo" />
              <span>{s.panelKicker}</span>
            </div>
            <h1 className="signup-panel-title">{s.panelTitle}</h1>
            <p className="signup-panel-text">{s.panelText}</p>
            <div className="signup-panel-plan">
              <span>{s.interestLabel}</span>
              <strong>{planLabel}</strong>
            </div>
            <ul className="signup-panel-points">
              {s.panelPoints.map((point) => (
                <li key={point}>
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </aside>

          <div className="signup-card">
            {done ? (
              <div className="signup-success">
                <div className="signup-success-icon" aria-hidden="true">
                  <i className="fa-solid fa-envelope-circle-check" />
                </div>
                <h2>{done.pending ? s.pendingTitle : s.successTitle}</h2>
                <p>{done.pending ? s.pendingBody : s.successBody}</p>
                {done.adminLogin ? (
                  <p className="signup-email-note">
                    {s.successLoginPrefix} <strong>{done.adminLogin}</strong>
                  </p>
                ) : null}
                {done.emailSent ? (
                  <p className="signup-email-note">{s.pendingEmailSent}</p>
                ) : (
                  <p className="signup-email-note signup-email-note-muted">
                    {s.pendingEmailSkipped}
                  </p>
                )}
                <Link className="signup-home-link" to="/">
                  {s.backHome}
                </Link>
              </div>
            ) : (
              <>
                <div className="signup-card-head">
                  <h2 className="signup-form-title">{s.title}</h2>
                  <p className="signup-sub">{s.formIntro}</p>
                </div>
                <form className="signup-form" onSubmit={onSubmit} noValidate>
                  <label className="signup-field">
                    <span className="signup-field-label">{s.businessName}</span>
                    <div className="signup-input-wrap">
                      <FieldIcon name="fa-store" />
                      <input
                        type="text"
                        value={businessName}
                        onChange={(e) => onBusinessNameChange(e.target.value)}
                        placeholder={s.businessNamePlaceholder}
                        required
                        maxLength={120}
                        autoComplete="organization"
                      />
                    </div>
                  </label>

                  <label className="signup-field">
                    <span className="signup-field-label">{s.category}</span>
                    <div className="signup-input-wrap signup-select-wrap">
                      <FieldIcon name="fa-tags" />
                      <select
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        required
                      >
                        <option value="" disabled>
                          {s.categoryPlaceholder}
                        </option>
                        {categoryOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <i className="fa-solid fa-chevron-down signup-select-caret" aria-hidden="true" />
                    </div>
                  </label>

                  <label className="signup-field">
                    <span className="signup-field-label">{s.country}</span>
                    <CountryPicker
                      value={countryCode}
                      onChange={setCountryCode}
                      disabled={busy}
                    />
                    <span className="signup-field-hint">
                      {s.countryHint.replace(
                        "{{currency}}",
                        getCountryPreset(countryCode).baseCurrency
                      )}
                    </span>
                  </label>

                  <label className="signup-field">
                    <span className="signup-field-label">{s.address}</span>
                    <div className="signup-input-wrap">
                      <FieldIcon name="fa-location-dot" />
                      <input
                        type="text"
                        value={businessAddress}
                        onChange={(e) => setBusinessAddress(e.target.value)}
                        placeholder={s.addressPlaceholder}
                        maxLength={200}
                        autoComplete="street-address"
                      />
                    </div>
                    <span className="signup-field-hint">{s.addressHint}</span>
                  </label>

                  <label className="signup-field">
                    <span className="signup-field-label">{s.login}</span>
                    <div
                      className={`signup-input-wrap signup-login-wrap${loginHasError ? " is-error" : ""}${
                        loginAvailability === "available" ? " is-ok" : ""
                      }`}
                    >
                      <span className="signup-login-prefix" aria-hidden="true">
                        admin@
                      </span>
                      <input
                        type="text"
                        value={loginShortCode}
                        onChange={(e) => onShortCodeChange(e.target.value)}
                        placeholder={loginPlaceholder}
                        required
                        maxLength={32}
                        autoComplete="username"
                        spellCheck={false}
                        aria-invalid={loginHasError}
                        aria-describedby="signup-login-hint"
                      />
                    </div>
                    <span className="signup-field-hint" id="signup-login-hint">
                      {s.loginHint}
                      {loginShortCode ? (
                        <>
                          {" "}
                          <strong>{adminLogin}</strong>
                        </>
                      ) : null}
                    </span>
                    {loginAvailability === "checking" ? (
                      <span className="signup-field-hint signup-login-checking">
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                        {s.loginChecking}
                      </span>
                    ) : null}
                    {loginAvailability === "taken" ? (
                      <span className="signup-field-error">{s.loginTaken}</span>
                    ) : null}
                    {loginAvailability === "invalid" && loginShortCode ? (
                      <span className="signup-field-error">{s.loginInvalid}</span>
                    ) : null}
                  </label>

                  <label className="signup-field">
                    <span className="signup-field-label">{s.email}</span>
                    <div className="signup-input-wrap">
                      <FieldIcon name="fa-envelope" />
                      <input
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        placeholder={s.emailPlaceholder}
                        required
                        autoComplete="email"
                      />
                    </div>
                    <span className="signup-field-hint">{s.emailHint}</span>
                  </label>

                  <div className="signup-form-row">
                    <label className="signup-field">
                      <span className="signup-field-label">{s.password}</span>
                      <div
                        className={`signup-input-wrap signup-input-wrap-password${
                          passwordMismatch ? " is-error" : ""
                        }`}
                      >
                        <FieldIcon name="fa-lock" />
                        <input
                          type={showPassword ? "text" : "password"}
                          value={adminPassword}
                          onChange={(e) => {
                            setAdminPassword(e.target.value);
                            if (error === s.passwordMismatch) setError("");
                          }}
                          placeholder={s.passwordPlaceholder}
                          required
                          minLength={8}
                          autoComplete="new-password"
                          aria-invalid={passwordMismatch}
                        />
                        <PasswordToggle
                          shown={showPassword}
                          onToggle={() => setShowPassword((v) => !v)}
                          showLabel={s.showPassword}
                          hideLabel={s.hidePassword}
                        />
                      </div>
                    </label>
                    <label className="signup-field">
                      <span className="signup-field-label">{s.passwordConfirm}</span>
                      <div
                        className={`signup-input-wrap signup-input-wrap-password${
                          passwordMismatch ? " is-error" : ""
                        }`}
                      >
                        <FieldIcon name="fa-lock" />
                        <input
                          type={showPassword2 ? "text" : "password"}
                          value={adminPassword2}
                          onChange={(e) => {
                            setAdminPassword2(e.target.value);
                            if (error === s.passwordMismatch) setError("");
                          }}
                          placeholder={s.passwordConfirmPlaceholder}
                          required
                          minLength={8}
                          autoComplete="new-password"
                          aria-invalid={passwordMismatch}
                        />
                        <PasswordToggle
                          shown={showPassword2}
                          onToggle={() => setShowPassword2((v) => !v)}
                          showLabel={s.showPassword}
                          hideLabel={s.hidePassword}
                        />
                      </div>
                    </label>
                  </div>
                  {passwordMismatch ? (
                    <p className="signup-field-error">{s.passwordMismatch}</p>
                  ) : (
                    <p className="signup-field-hint">{s.passwordHint}</p>
                  )}

                  {siteKeyLoading ? (
                    <p className="signup-field-hint">{s.captchaLoading}</p>
                  ) : captchaEnabled ? (
                    <div className="signup-turnstile">
                      <div ref={turnstile.hostRef} />
                      {!turnstile.ready ? (
                        <span className="signup-field-hint">{s.captchaLoading}</span>
                      ) : null}
                    </div>
                  ) : (
                    <p className="signup-field-hint signup-captcha-dev">
                      {s.captchaDevNote}
                    </p>
                  )}

                  <label className="signup-hp" aria-hidden="true">
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </label>

                  {error ? (
                    <p className="signup-error">
                      <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                      {error}
                    </p>
                  ) : null}

                  <button type="submit" className="signup-submit" disabled={createDisabled}>
                    {busy ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                        {s.submitting}
                      </>
                    ) : (
                      <>
                        {s.submit}
                        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                      </>
                    )}
                  </button>
                  <p className="signup-legal">
                    {s.legalPrefix}{" "}
                    <Link to="/politique-legale">{s.legalLink}</Link>.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
